import styled from 'styled-components';
import {
  Flex,
  FlexColumn,
  QuickFilterChip,
} from '@elementary/common-ui/components';
import {
  capitalize,
  isElementaryCloudTest,
} from '@elementary/common-ui/utils/common';
import { Typography } from '@mui/material';
import { qualityDimensions } from 'shared/constants';
import { QualityDimension, TestTemplateMetadata } from 'shared/types';

const Wrapper = styled(FlexColumn)`
  min-width: 248px;
  width: 248px;
  padding: 24px 12px 0 12px;
  gap: 24px;
`;

const FiltersWrapper = styled(FlexColumn)`
  align-items: flex-start;
  gap: 8px;
`;

const GroupWrapper = styled(FlexColumn)`
  gap: 8px;
  padding-left: 18px;
`;

const HeaderWrapper = styled(Flex)`
  flex-direction: column;
  gap: 4px;
  padding-left: 18px;
`;

export type QuickFilterValue = 'all' | 'popular';
export type SourceFilterValue = 'dbt' | 'elementary cloud';
export type CategoryFilterValue = QualityDimension | 'other';

export type TestCatalogFilters = {
  quickFilter: QuickFilterValue;
  sourceFilter?: SourceFilterValue;
  categoryFilter?: CategoryFilterValue;
};

type FilterOption<T extends string> = {
  label: string;
  value: T;
  count: number;
};

type FilterGroupProps<T extends string> = {
  title: string;
  options: FilterOption<T>[];
  selectedValue?: T;
  onChange: (val: T) => void;
};

type Props = {
  value: TestCatalogFilters;
  onChange: (val: TestCatalogFilters) => void;
  elementaryTests: TestTemplateMetadata[];
  testsTemplatesData?: TestTemplateMetadata[];
};

const FilterGroup = <T extends string>({
  title,
  options,
  selectedValue,
  onChange,
}: FilterGroupProps<T>) => (
  <GroupWrapper>
    <Typography variant="body2">{title}</Typography>
    <FiltersWrapper>
      {options.map((option) => (
        <QuickFilterChip
          key={option.value}
          value={option.value}
          label={`${option.label} (${option.count})`}
          filter={selectedValue || ''}
          onClick={() => onChange(option.value)}
        />
      ))}
    </FiltersWrapper>
  </GroupWrapper>
);

const QualityDimensionTabs = ({
  elementaryTests,
  testsTemplatesData = [],
  value,
  onChange,
}: Props) => {
  const allTemplates = [...testsTemplatesData, ...elementaryTests];

  const getTemplateCount = (
    predicate: (template: TestTemplateMetadata) => boolean,
  ) => allTemplates.filter(predicate).length;

  const quickFilterOptions: FilterOption<QuickFilterValue>[] = [
    { label: 'All', value: 'all', count: allTemplates.length },
    {
      label: 'Popular',
      value: 'popular',
      count: getTemplateCount((template) => template.popularity === 1),
    },
  ];
  const sourceFilterOptions: FilterOption<SourceFilterValue>[] = [
    {
      label: 'dbt',
      value: 'dbt',
      count: getTemplateCount(
        (template) => !isElementaryCloudTest(template.test_name),
      ),
    },
    {
      label: 'Elementary cloud',
      value: 'elementary cloud',
      count: getTemplateCount((template) =>
        isElementaryCloudTest(template.test_name),
      ),
    },
  ];
  const categoryFilterOptions: FilterOption<CategoryFilterValue>[] = [
    ...qualityDimensions
      .map<FilterOption<CategoryFilterValue>>((qualityDimension) => ({
        label: capitalize(qualityDimension),
        value: qualityDimension,
        count: getTemplateCount(
          (template) => template.quality_dimension === qualityDimension,
        ),
      }))
      .filter(({ count }) => count > 0),
    {
      label: 'Other',
      value: 'other',
      count: getTemplateCount((template) => !template.quality_dimension),
    },
  ];

  return (
    <Wrapper>
      <HeaderWrapper>
        <Typography variant="body2">Filter by:</Typography>
        <Typography sx={{ color: 'common.gray2' }} variant="body2">
          Combine filters across groups
        </Typography>
      </HeaderWrapper>
      <FilterGroup
        title="Quick filters"
        options={quickFilterOptions}
        selectedValue={value.quickFilter}
        onChange={(quickFilter) => onChange({ ...value, quickFilter })}
      />
      <FilterGroup
        title="Source"
        options={sourceFilterOptions}
        selectedValue={value.sourceFilter}
        onChange={(sourceFilter) =>
          onChange({
            ...value,
            sourceFilter:
              value.sourceFilter === sourceFilter ? undefined : sourceFilter,
          })
        }
      />
      <FilterGroup
        title="Categories"
        options={categoryFilterOptions}
        selectedValue={value.categoryFilter}
        onChange={(categoryFilter) =>
          onChange({
            ...value,
            categoryFilter:
              value.categoryFilter === categoryFilter
                ? undefined
                : categoryFilter,
          })
        }
      />
    </Wrapper>
  );
};

export default QualityDimensionTabs;
